/**
 * @file     seoChatSurface.ts
 * @purpose  Allowlisted SEO surfaces that may open the contained Relta chat
 *           while the site-wide AI chat flag remains off. Context is selected
 *           server-side, so a client cannot inject trusted page instructions.
 */

import { RATES_VERIFIED_ON } from '@/services/buyer/buyingCosts';
import { TAX_RATES_VERIFIED_ON } from '@/services/tax/bcPropertyTaxes';
import { featureFlags } from '@/core/config';
import { BC_CITIES_BY_SLUG } from '@/core/config/bcCities';
import { BC_REGIONS } from '@/core/config/bcAreas';

export const SEO_CHAT_SURFACE_IDS = [
  'properties-filter-search',
  'property-finder',
  'buying-costs-bc',
  'affordability-bc',
  'bc-property-taxes',
  'depreciation-reports',
  'home-search-hubs',
  'home-search-facets',
  'market-data-hubs',
  'mortgage-planning',
] as const;

export type SeoChatSurfaceId = (typeof SEO_CHAT_SURFACE_IDS)[number];

/**
 * Prompt-safe labels for the public /homes/[city]/[facet] vocabulary. This is
 * deliberately a closed map rather than client-provided prose. The parity
 * test against the page registry prevents a new indexed facet from silently
 * losing its trusted assistant context.
 */
export const SEO_HOME_SEARCH_FACETS: Readonly<Record<string, string>> = {
  'finished-basement': 'homes with a finished basement',
  suite: 'homes with a secondary suite',
  waterfront: 'waterfront homes',
  'with-view': 'homes with a view',
  'with-pool': 'homes with a pool',
  'with-garage': 'homes with a garage',
  'air-conditioning': 'homes with air conditioning',
  fireplace: 'homes with a fireplace',
  'open-concept': 'open-concept homes',
  renovated: 'renovated homes',
  'with-hot-tub': 'homes with a hot tub',
  'corner-unit': 'corner units or corner lots',
  'multi-generational': 'multi-generational homes',
  'near-transit': 'homes near rapid transit',
  walkable: 'walkable homes',
  'bike-friendly': 'bike-friendly homes',
  'near-schools': 'homes near well-rated schools',
  'near-water': 'homes near water',
  'under-500k': 'homes under $500K',
  '500k-to-750k': 'homes from $500K to $750K',
  '750k-to-1m': 'homes from $750K to $1M',
  '1m-to-1-5m': 'homes from $1M to $1.5M',
  'over-1-5m': 'homes over $1.5M',
  '2-bedroom': '2 bedroom homes',
  '3-bedroom': '3 bedroom homes',
  '4-bedroom': '4 bedroom homes',
  condo: 'condos and apartments',
  detached: 'detached homes',
  townhouse: 'townhouses',
  duplex: 'duplexes and half duplexes',
  manufactured: 'manufactured and mobile homes',
  'low-strata-fee': 'homes with a low strata fee',
  rancher: 'ranchers and bungalows',
  penthouse: 'penthouses',
  'character-home': 'character and heritage homes',
  cabin: 'cabins and cottages',
  presale: 'presale and assignment homes',
  'new-construction': 'newly built homes',
};

/**
 * SEO assistants use the same filter-state presentation broker as AI Search.
 * The model may shape a search, receive counts, and propose user-side actions;
 * listing-specific or listing-derived read tools stay out of its tool schema.
 * Full cards are fetched and rendered client-side from the audited API.
 */
const COMMON_SEO_CAPABILITIES = [
  'update_search',
  'count_listings',
  'estimate_mortgage',
  'current_rates',
  'market_trends',
  'market_chart',
  'readiness_score',
  'my_saved_overview',
  'save_home',
  'save_search',
  'ask_about_home',
  'schedule_showing',
  'ask_ray',
] as const;

const SEO_SAFE_CAPABILITIES: Record<SeoChatSurfaceId, ReadonlySet<string>> = {
  'properties-filter-search': new Set([
    'update_search',
    'count_listings',
  ]),
  // The property finder's follow-up conversation: reshape the search, move the
  // map, and redo the lender math deterministically. No listing reads and no
  // save proposals, since the page owns its own list and its own save.
  'property-finder': new Set([
    'update_search',
    'count_listings',
    'lender_ceiling',
    'affordability_check',
    'estimate_mortgage',
    'current_rates',
    'set_map_layers',
  ]),
  'buying-costs-bc': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'affordability_check',
    'estimate_buying_costs',
  ]),
  'affordability-bc': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'affordability_check',
  ]),
  'bc-property-taxes': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'estimate_buying_costs',
    'estimate_bc_property_taxes',
  ]),
  'depreciation-reports': new Set([
    ...COMMON_SEO_CAPABILITIES,
  ]),
  'home-search-hubs': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'affordability_check',
  ]),
  'home-search-facets': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'affordability_check',
  ]),
  'market-data-hubs': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'affordability_check',
  ]),
  'mortgage-planning': new Set([
    ...COMMON_SEO_CAPABILITIES,
    'affordability_check',
    'estimate_buying_costs',
  ]),
};

export type SeoChatFlagSnapshot = Pick<
  typeof featureFlags,
  | 'aiChat'
  | 'aiMapSearch'
  | 'aiSeoChat'
  | 'aiSeoChatBuyingCosts'
  | 'aiSeoChatAffordability'
  | 'aiSeoChatTaxes'
  | 'aiSeoChatDepreciation'
  | 'aiSeoChatHomes'
  | 'aiSeoChatHomeFacets'
  | 'aiSeoChatMarketData'
  | 'aiSeoChatFinance'
>;

/**
 * One rollout decision shared by UI and API. Hiding a family in the browser is
 * not the security boundary: the route calls this again before any LLM work.
 */
export function seoChatSurfaceEnabled(
  surface: SeoChatSurfaceId,
  flags: SeoChatFlagSnapshot = featureFlags,
): boolean {
  if (surface === 'properties-filter-search' || surface === 'property-finder') {
    return flags.aiChat && flags.aiMapSearch;
  }
  if (!flags.aiSeoChat) return false;
  if (surface === 'buying-costs-bc') return flags.aiSeoChatBuyingCosts;
  if (surface === 'affordability-bc') return flags.aiSeoChatAffordability;
  if (surface === 'bc-property-taxes') return flags.aiSeoChatTaxes;
  if (surface === 'depreciation-reports') return flags.aiSeoChatDepreciation;
  if (surface === 'home-search-facets') return flags.aiSeoChatHomeFacets;
  if (surface === 'market-data-hubs') return flags.aiSeoChatMarketData;
  if (surface === 'mortgage-planning') return flags.aiSeoChatFinance;
  return flags.aiSeoChatHomes;
}

export function isSeoChatSurfaceId(value: unknown): value is SeoChatSurfaceId {
  return typeof value === 'string'
    && (SEO_CHAT_SURFACE_IDS as readonly string[]).includes(value);
}

export function capabilityAllowedOnSeoSurface(surface: SeoChatSurfaceId, name: string): boolean {
  return SEO_SAFE_CAPABILITIES[surface].has(name);
}

export function seoChatSurfaceAllowsListingPresentation(surface: SeoChatSurfaceId | null): boolean {
  return surface !== 'properties-filter-search' && surface !== 'property-finder';
}

export function seoChatSurfaceContext(
  surface: SeoChatSurfaceId | null,
  locationSlug?: string,
  facetSlug?: string,
): string | undefined {
  const city = locationSlug ? BC_CITIES_BY_SLUG.get(locationSlug) : undefined;
  const region = locationSlug ? BC_REGIONS.find((item) => item.slug === locationSlug) : undefined;
  const facet = facetSlug ? SEO_HOME_SEARCH_FACETS[facetSlug] : undefined;
  const pageTopic = facetSlug ? SEO_CHAT_PAGE_TOPICS[facetSlug] : undefined;

  if (surface === 'properties-filter-search') {
    return [
      'Current surface: AI-assisted search on /properties.',
      'This assistant only translates the visitor request into filter values and reports aggregate result counts.',
      'Use update_search to apply filters and count_listings only to test a possible filter set.',
      'Never inspect, choose, recommend, present, focus, save, summarize, or describe an individual listing.',
      'Never use the present or focus action. End with ask or answer after the filters are applied.',
      'The separate property search loads MLS and VOW results after the filters change. The model receives no MLS or VOW listing data.',
    ].join(' ');
  }

  if (surface === 'property-finder') {
    return [
      'Current surface: the Relta property finder, after the buyer has seen their straight answer.',
      "The applied priceMax is the buyer's lender ceiling, computed by the page from their stated income and down payment. The page owns that number: never raise priceMax, even when lender_ceiling returns a higher figure. Lower it only when the buyer asks for a lower price.",
      'If lender_ceiling returns a different ceiling, state it once as an estimate and tell the buyer to use "Change these numbers" on the page to update their search; do not apply it.',
      'You are not told the buyer\'s income or down payment. Never guess, infer or reuse a number for either. Only call lender_ceiling when the buyer states both a full monthly household income and a full down payment in this conversation; if a what-if names only a change ("more down", "a raise"), ask for the two full figures first.',
      'Help them trade off lifestyle against price: use update_search for location, beds, property type and lifestyle pills (near-transit, good-schools, walkable, bikeable, near-water), and count_listings to test a change before applying it.',
      'Use set_map_layers to show transit, schools, parks or density when it explains a trade-off.',
      'Never do mortgage or affordability arithmetic in prose; use lender_ceiling, affordability_check or estimate_mortgage.',
      'Never inspect, choose, recommend, present, focus, save or describe an individual listing. The page shows the homes and owns the save. End with ask or answer.',
      'If a change would leave zero homes, say so and suggest the smallest change that brings results back.',
      "Write at a 5th grade reading level: short sentences and everyday words. Say 'top price', not 'lender ceiling'; 'monthly cost', not 'carry'; 'paid over 25 years', not 'amortization'.",
      'The model receives filter state and aggregate counts only, never MLS or VOW rows.',
    ].join(' ');
  }

  if (surface === 'market-data-hubs') {
    const place = city?.name ?? region?.label ?? 'British Columbia';
    return [
      `Current page: ${pageTopic ?? 'Public real estate market data'} in ${place}.`,
      'Use public aggregate market tools for current price, inventory, rate, and trend questions.',
      'Use update_search and count_listings when the visitor wants to narrow or see homes.',
      city || region
        ? `Keep ${place} as the geographic anchor unless the visitor explicitly changes it.`
        : 'Ask for a city or neighbourhood when location is needed.',
      facetSlug === 'off-market'
        ? 'Sold, pending, expired, and other VOW records require registration. Never claim access to a gated record or reveal a sold figure from conversation context.'
        : 'Do not claim to have inspected a listing, score, building record, or event schedule unless the browser has rendered the relevant public card.',
      'The model receives page identity, filter state, aggregate counts, and opaque listing keys only. It never receives MLS or VOW rows.',
    ].join(' ');
  }

  if (surface === 'mortgage-planning') {
    return [
      'Current page: Mortgage payment and affordability planning in British Columbia.',
      'Use estimate_mortgage for payment, amortization, and rate scenarios. Never calculate them in prose.',
      'Use current_rates before stating a current policy, prime, or mortgage rate.',
      'Use affordability_check for income, debt-ratio, or carrying-cost scenarios and estimate_buying_costs for cash-to-close questions.',
      'State assumptions briefly and ask one useful follow-up when a key input is missing.',
      'This page contains no MLS or VOW listing rows.',
    ].join(' ');
  }

  if (surface === 'affordability-bc') {
    const place = city?.name ?? 'British Columbia';
    return [
      `Current page: Home affordability in ${place}.`,
      'Use affordability_check for any price, income, down-payment, debt-ratio, or carrying-cost scenario. Never calculate those figures in prose.',
      'Use current_rates before stating a current rate. Keep the policy target, Bank Rate, prime, bond yields, and consumer mortgage rates distinct.',
      city
        ? `If the buyer asks to see homes without naming another location, keep ${city.name} as the geographic anchor in the filter state.`
        : 'Do not assume a city until the buyer names one.',
      'The page combines public census income figures with current public asking-price aggregates. It does not expose listing rows to the model.',
      'Published page ratios are not in the conversation context. Do not repeat or invent a page figure unless the buyer states it or a tool returns it.',
    ].join(' ');
  }

  if (surface === 'bc-property-taxes') {
    return [
      'Current page: Property taxes in British Columbia.',
      `The statutory tax rules used by this page were verified on ${TAX_RATES_VERIFIED_ON}.`,
      'Use estimate_bc_property_taxes for numeric property transfer tax or speculation and vacancy tax questions. Never do tax arithmetic in prose.',
      'Do not estimate capital gains tax. Explain briefly which missing facts matter and suggest confirming the result with an accountant.',
      'Keep property transfer tax, annual property tax, speculation and vacancy tax, and capital gains tax distinct.',
      'This page uses public statutory rules and contains no MLS or VOW listing rows.',
    ].join(' ');
  }

  if (surface === 'depreciation-reports') {
    const place = city?.name ?? 'British Columbia';
    return [
      `Current page: Strata depreciation reports in ${place}.`,
      'Explain depreciation reports, contingency reserve funds, special levies, building-age risk, and strata fees in plain language.',
      'Use public market trend tools for current aggregate market questions. Do not claim to have read a specific building report, Form B, minutes, bylaws, or listing record.',
      city
        ? `If the buyer asks to see strata homes without naming another location, keep ${city.name} as the geographic anchor in the filter state.`
        : 'Do not assume a city until the buyer names one.',
      'The model receives no MLS or VOW listing rows and cannot determine whether a specific strata is compliant.',
    ].join(' ');
  }

  if (surface === 'home-search-hubs' || surface === 'home-search-facets') {
    const place = city?.name ?? 'British Columbia';
    return [
      `Current page: Homes and neighbourhood search in ${place}.`,
      facet
        ? `This page is specifically for ${facet}. Preserve that criterion in the filter state unless the buyer explicitly changes or removes it.`
        : 'No page-level home feature or price criterion is fixed.',
      'Use update_search to translate the buyer request into filter state, then count_listings before presenting a home.',
      city
        ? `Keep ${city.name} as the geographic anchor unless the buyer explicitly names another place.`
        : 'Ask for a city or neighbourhood when the buyer has not named one.',
      'When the buyer asks to see a home, use the presentation action. The browser fetches and renders full listing cards from the audited listings API.',
      'The model receives filters, aggregate counts, and opaque listing keys only. It never receives MLS or VOW listing rows.',
      'Do not describe an individual home until its card is visible in the conversation.',
    ].join(' ');
  }

  if (surface !== 'buying-costs-bc') return undefined;

  const place = city?.name ?? 'British Columbia';

  return [
    `Current page: Cost of buying a home in ${place}.`,
    `The statutory rates used by this page were verified on ${RATES_VERIFIED_ON}.`,
    'Use estimate_buying_costs for any price-specific transfer-tax, minimum-down-payment, closing-cost, or cash-to-close question.',
    city
      ? `If the buyer asks to see homes without naming another location, keep ${city.name} as the geographic anchor in the filter state.`
      : 'Do not assume a city until the buyer names one.',
    'For current rates, market conditions, or selling-timing questions, use current_rates and the available public market trend tools. Never answer those from model memory.',
    'For a broad "is it a good time to sell" question, render one market_chart using the Greater Vancouver composite benchmark unless the buyer named another area or home type. Explain briefly that the aggregate cannot price their specific home, then ask for the neighbourhood and home type.',
    'Third-party cost figures are illustrative, not quotes. Eligibility for an exemption must be confirmed with a conveyancer or the Province of British Columbia.',
    'This page context is first-party guidance. It contains no MLS or VOW listing rows.',
  ].join(' ');
}

export const SEO_CHAT_PAGE_TOPICS: Readonly<Record<string, string>> = {
  'hot-deals': 'Homes scored against comparable sales and market context',
  'market-report': 'Real estate market reports',
  'price-drops': 'Listings with recent asking-price reductions',
  'new-listings': 'Homes newly listed this week',
  'open-houses': 'Upcoming open houses',
  buildings: 'Homes grouped by building or complex',
  'off-market': 'Off-market and registrant-gated market records',
  'mortgage-calculator': 'Mortgage payment and amortization planning',
};
